import { AfterContentInit, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Observable, Subject } from "rxjs";
import { GridsterItem }  from 'angular-gridster2';
import { Select, Store } from "@ngxs/store";
import { Dialog, Snackbar, ViewComponent } from "@core/material";
import { AnyReportTemplate, ChartComponent, COMPONENT_CONFIG_DEFAULT, COMPONENT_OVERLAP_WARNING, ComponentGeometry, GanttComponent, ImageComponent, IndexComponent, ReportComponentType, TableComponent, TextComponent, findFreePosition, resolveGridDensity, scaleGridOptions } from "@core/data/types/advanced-report";
import { ReportComponentFormDialog, ReportComponentFormDialogData } from "../report-component-form/report-component-form.dialog";
import { AdvancedReportState } from "./report-state/advanced-report.state";
import { AddReportPage, ApplyReportTemplate, ExtractReportTemplate, InitReportGenerator, LoadReportGenerator, SetReportFilter, ToggleEditMode, UpdateReportTitle } from "./report-state/advanced-report.state-actions";
import { MatDrawer } from "@angular/material/sidenav";
import { FontLoaderService } from "@core/fonts/font-loader.service";
import { EditModeHintService } from "../../services/edit-mode-hint.service";
import { Status } from "@core/data";
import { AdvancedReportModel, AdvancedReportStateModel } from "./report-state/advanced-report.state-models";
import { AddReportComponent } from "../report-page/report-page-state/report-page.state-actions";
import { LoadComponentOptions } from "../report-components/report-component.state-actions";
import { ComponentState } from "../report-components/report-component.state";
import { ReportFilterDialog, ReportFilterDialogData } from "../report-dialogs/report-filter-dialog/report-filter-form.dialog";
import { take, takeUntil } from "rxjs/operators";
import { FormControl } from "@angular/forms";
import { PageState } from "../report-page/report-page-state/report-page.state";
import { PageModel, PageStateModel } from "../report-page/report-page-state/report-page.state-models";
import { AdvancedReportShareDialog, AdvancedReportShareDialogData } from "../report-dialogs/report-share-dialog/advanced-report-share.dialog";

enum RightSideBar {
    TEMPLATES = "TEMPLATES",
    UNSET = "UNSET"
}

@Component({
    selector: 'pv-advanced-reports',
    templateUrl: 'advanced-report.view.html',
    changeDetection: ChangeDetectionStrategy.Default,
    preserveWhitespaces: false,
    host: {
        class: 'pv-advanced-report'
    }
})
export class ReportView implements OnInit, OnDestroy, AfterViewInit {

    //? MAIN STATE
    reportState$: Observable<AdvancedReportModel>;

    @Select(AdvancedReportState)
    state$: Observable<AdvancedReportStateModel>;

    @Select(PageState)
    pageState$: Observable<PageStateModel>;

    @ViewChild(ViewComponent, {static: false})
    viewComponent: ViewComponent;

    @ViewChild('leftDrawer', { static: false, read: MatDrawer }) leftDrawerRef: MatDrawer;
    @ViewChild('rightDrawer', { static: false, read: MatDrawer }) rightDrawerRef: MatDrawer;

    leftHemisphere: boolean = false;
    rightHemisphere: boolean = false;

    reportImages: Array<GridsterItem>;
    reportTextItems: Array<GridsterItem>;

    status: Status = Status.UNINITIALIZED;

    tempRef: string = "";
    tempText: string = "";

    rowMax: number = 37;
    colMax: number = 26;

    @Input()
    editMode: boolean = true;

    @Input()
    preview: boolean = false;

    @Input()
    reportKey: string;

    @Input()
    ownerOrgKey: string;

    @Input()
    shareKey: string = null;

    showRightContent: boolean = false;
    rightActive: RightSideBar = RightSideBar.UNSET;

    filterTrigger: boolean = false;
    templateControl: FormControl = new FormControl(null);

    private _destroy$ = new Subject();

    constructor(
        private _route: ActivatedRoute,
        private _router: Router,
        private _store: Store,
        private _dialog: Dialog,
        private _changes: ChangeDetectorRef,
        private _snackbar: Snackbar,
        private _fontLoader: FontLoaderService,
        private _editHint: EditModeHintService,
    ) {}

    // Brief flash on the edit toggle when a component is touched in preview mode.
    editFlash: boolean = false;

    ngOnInit() {
        this._route.paramMap
            .subscribe(params => {
                if (!this.preview) {
                    this.ownerOrgKey = params.get('orgKey');
                    this.reportKey = params.get('reportKey');
                }

                this._store.dispatch(new InitReportGenerator(this.ownerOrgKey, this.reportKey, this.editMode, this.shareKey));
            });

        this.reportImages = [];
        this.reportTextItems = [];

        this.reportState$ = this._store.select(AdvancedReportState.getReport(this.reportKey));

        this._editHint.flash$.pipe(
            takeUntil(this._destroy$)
        ).subscribe(() => {
            this.editFlash = true;
            this._changes.markForCheck();
            setTimeout(() => { this.editFlash = false; this._changes.markForCheck(); }, 900);
        });

        this.state$.pipe(
            takeUntil(this._destroy$)
        ).subscribe((state: AdvancedReportStateModel) => {
            this.editMode = state.editMode;
        });

        this.templateControl.valueChanges.pipe(
            takeUntil(this._destroy$)
        ).subscribe((template: AnyReportTemplate) => {
            this.applyTemplates(template);
        });

        this.reportState$.pipe(
            takeUntil(this._destroy$)
        ).subscribe((state: AdvancedReportModel) => {
            if (state.status !== Status.OK) return;

            if (state.report.filter == null && !this.filterTrigger) {
                this.filterTrigger = true;

                // New report: open the options tray first, then let the filter dialog take focus
                // a beat later so the two don't appear simultaneously.
                if (this.rightDrawerRef) this.rightDrawerRef.open();
                setTimeout(() => this.openFilterDialog(), 180);
            }
        })
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    ngAfterViewInit() {
        if (this.preview) return;

        this.leftHemisphere = this.leftDrawerRef.opened || false;
        this.rightHemisphere = this.rightDrawerRef.opened || false;

        this.showRightContent = this.rightHemisphere;

        this.leftDrawerRef.openedChange.subscribe(left => {
            this.leftHemisphere = left;
        })

        this.rightDrawerRef.openedChange.subscribe(right => {
            this.rightHemisphere = right;
            this.showRightContent = right;
        })
    }

    updateReportTitle(title: string) {
        this._store.dispatch(new UpdateReportTitle(title));
    }

    extractTemplate() {
        this._dialog.confirm(
            'Extract Template',
            'Are you sure you want to create a new template from the current Report?',
            'Confirm',
            'Cancel',
            false,
            'reporting-theme'
        ).afterClosed().subscribe(res => {
            if (!res) return;

            this._store.dispatch(new ExtractReportTemplate)
        });
    }


    applyTemplates(template: AnyReportTemplate) {
        this._dialog.confirm(
            'Apply Template',
            `Are you sure you want to apply ${template.label} to the current Report?`,
            'Confirm',
            'Cancel',
            false,
            'reporting-theme'
        ).afterClosed().subscribe(res => {
            this.templateControl.setValue(null, { emitEvent: false }); //? clear the control

            if (!res) return;

            this._store.dispatch(new ApplyReportTemplate(template));
        });
    }

    toggleTemplates() {
        this.toggleRightSidebar(RightSideBar.TEMPLATES)
    }

    // Every add*Item() handler below used to hardcode x:0, y:0 with no collision check, so a new
    // component silently failed to render (gridster marks it notPlaced) whenever anything already
    // occupied that spot on the default layer. Mirrors duplicateComponent()'s/
    // resolveLayerSwitchPlacement()'s room check in report-component.view.ts, but this component has
    // no existing-component instance to check from (the component doesn't exist yet) — so it fetches
    // its own siblings and calls the same shared, pure findFreePosition() directly.
    //
    // Bounds come from the target page's actual live-scaled grid (orientation + density), not this
    // file's own rowMax/colMax fields (37/26) or the raw unscaled PageShortEdge/PageLongEdge (31/44)
    // — both are wrong for a reoriented or Dense-density page; scaleGridOptions/resolveGridDensity is
    // the same computation ReportPageView itself uses to render the grid.
    //
    // New (non-duplicate) components always create on COMPONENT_CONFIG_DEFAULT.layerIndex — the "Add
    // Component" dialog has no layer selector, and getNewReportOptions() (report-page.state.ts)
    // confirms non-duplicate components always get {...COMPONENT_CONFIG_DEFAULT} as their config.
    private resolveNewComponentPlacement(pageKey: string, width: number, height: number): { x: number, y: number } | null {
        const pageModel: PageModel = this._store.selectSnapshot(PageState.getPage(pageKey));
        const page = pageModel?.page;
        // page.options can legitimately be null for a page whose options haven't loaded/synced yet
        // (see the page-orientation investigation earlier this session) — can't determine real bounds
        // without it, so refuse rather than guess.
        if (!page?.options?.config?.gridOptions) return null;

        // Snapshotted fresh here rather than trusting a report object passed in from the click that
        // opened the dialog — the dialog can stay open long enough for density to change underneath
        // it, and ReportPageView (the actual grid renderer) already reads density this same way
        // rather than from a value captured earlier.
        const reportModel = this._store.selectSnapshot(AdvancedReportState.getReport(this.reportKey));
        const density = resolveGridDensity(reportModel?.report?.options?.config);
        const scaled = scaleGridOptions(page.options.config.gridOptions, density);

        const layerIndex = COMPONENT_CONFIG_DEFAULT.layerIndex;
        const componentKeys = pageModel.componentKeys ?? [];
        const entities = this._store.selectSnapshot(ComponentState.getEntities);

        const siblings = componentKeys.reduce((acc, key) => {
            const sibling = entities[key]?.component;
            if (!sibling) return acc;
            if (![sibling.x, sibling.y, sibling.width, sibling.height].every(Number.isFinite)) return acc;

            const siblingLayer = sibling.options?.config?.layerIndex ?? layerIndex;
            if (siblingLayer !== layerIndex) return acc;

            acc.push({ x: sibling.x, y: sibling.y, width: sibling.width, height: sibling.height });
            return acc;
        }, [] as ComponentGeometry[]);

        return findFreePosition(siblings, width, height, scaled.maxCols, scaled.maxRows, 0, 0);
    }

    // TODO refactor add item functions to generic function
    addImageItem(report: AdvancedReportModel) {
        if (!this.editModeCheck()) return;

        const data: ReportComponentFormDialogData = {
            dialogType: ReportComponentType.IMAGE,
            maxPages: report.pageKeys.length,
            maxColumns: this.colMax,
            maxRows: this.rowMax,
        }

        this._dialog.open(ReportComponentFormDialog, {
            data,
            panelClass: 'reporting-theme'
        }).afterClosed()
            .subscribe((result=> {
                if (!result.status) return;

                const { page, width, height, type } = result.data;

                const pageKey: string = report.pageKeys[page];

                const placement = this.resolveNewComponentPlacement(pageKey, width, height);
                if (!placement) return this._snackbar.warning(COMPONENT_OVERLAP_WARNING);

                const element: Partial<ImageComponent> = {
                    pageKey: pageKey,
                    height: height,
                    width: width,
                    type: type,
                    filter: null,
                    x: placement.x,
                    y: placement.y,
                }

                this._store.dispatch(new AddReportComponent(pageKey, element));
            }));
    }

    addTextItem(report: AdvancedReportModel) {
        if (!this.editModeCheck()) return;

        const data: ReportComponentFormDialogData = {
            dialogType: ReportComponentType.TEXT,
            maxPages: report.pageKeys.length,
            maxColumns: this.colMax,
            maxRows: this.rowMax,
        }

        this._dialog.open(ReportComponentFormDialog, {
            data,
            panelClass: 'reporting-theme'
        }).afterClosed()
            .subscribe((result=> {
                if (!result.status) return;

                const { page, width, height, type } = result.data;

                const pageKey: string = report.pageKeys[page];

                const placement = this.resolveNewComponentPlacement(pageKey, width, height);
                if (!placement) return this._snackbar.warning(COMPONENT_OVERLAP_WARNING);

                const element: Partial<TextComponent> = {
                    pageKey: pageKey,
                    height: height,
                    width: width,
                    filter: null,
                    x: placement.x,
                    y: placement.y,
                    type: type,
                }

                this._store.dispatch(new AddReportComponent(pageKey, element));
            }));
    }

    addChartItem(report: AdvancedReportModel) {
        if (!this.editModeCheck()) return;

        const data: ReportComponentFormDialogData = {
            dialogType: ReportComponentType.CHART,
            maxPages: report.pageKeys.length,
            maxColumns: this.colMax,
            maxRows: this.rowMax,
        }

        this._dialog.open(ReportComponentFormDialog, {
            data,
            panelClass: 'reporting-theme'
        }).afterClosed()
            .subscribe((result=> {
                if (!result.status) return;

                const { page, width, height, type } = result.data;

                const pageKey: string = report.pageKeys[page];

                const placement = this.resolveNewComponentPlacement(pageKey, width, height);
                if (!placement) return this._snackbar.warning(COMPONENT_OVERLAP_WARNING);

                const element: Partial<ChartComponent> = {
                    pageKey: pageKey,
                    height: height,
                    width: width,
                    filter: null,
                    x: placement.x,
                    y: placement.y,
                    type: type,
                }

                this._store.dispatch(new AddReportComponent(pageKey, element));
            }));
    }

    addIndexItem(report: AdvancedReportModel) {
        if (!this.editModeCheck()) return;

        const data: ReportComponentFormDialogData = {
            dialogType: ReportComponentType.INDEX,
            maxPages: report.pageKeys.length,
            maxColumns: this.colMax,
            maxRows: this.rowMax,
        }

        this._dialog.open(ReportComponentFormDialog, {
            data,
            panelClass: 'reporting-theme'
        }).afterClosed()
            .subscribe((result=> {
                if (!result.status) return;

                const { page, width, height, type } = result.data;

                const pageKey: string = report.pageKeys[page];

                const placement = this.resolveNewComponentPlacement(pageKey, width, height);
                if (!placement) return this._snackbar.warning(COMPONENT_OVERLAP_WARNING);

                const element: Partial<IndexComponent> = {
                    pageKey: pageKey,
                    height: height,
                    width: width,
                    filter: null,
                    x: placement.x,
                    y: placement.y,
                    type: type,
                }

                this._store.dispatch(new AddReportComponent(pageKey, element));
            }));
    }

    addTableItem(report: AdvancedReportModel) {
        if (!this.editModeCheck()) return;

        const data: ReportComponentFormDialogData = {
            dialogType: ReportComponentType.TABLE,
            maxPages: report.pageKeys.length,
            maxColumns: this.colMax,
            maxRows: this.rowMax,
        }

        this._dialog.open(ReportComponentFormDialog, {
            data,
            panelClass: 'reporting-theme'
        }).afterClosed()
            .subscribe((result=> {
                if (!result.status) return;

                const { page, width, height, type } = result.data;

                const pageKey: string = report.pageKeys[page];

                const placement = this.resolveNewComponentPlacement(pageKey, width, height);
                if (!placement) return this._snackbar.warning(COMPONENT_OVERLAP_WARNING);

                const element: Partial<TableComponent> = {
                    pageKey: pageKey,
                    height: height,
                    width: width,
                    filter: null,
                    x: placement.x,
                    y: placement.y,
                    type: type,
                }

                this._store.dispatch(new AddReportComponent(pageKey, element));
            }));
    }

    addGanttItem(report: AdvancedReportModel) {
        if (!this.editModeCheck()) return;

        const data: ReportComponentFormDialogData = {
            dialogType: ReportComponentType.GANTT,
            maxPages:   report.pageKeys.length,
            maxColumns: this.colMax,
            maxRows:    this.rowMax,
        };

        this._dialog.open(ReportComponentFormDialog, {
            data,
            panelClass: 'reporting-theme'
        }).afterClosed()
            .subscribe((result => {
                if (!result.status) return;

                const { page, width, height, type } = result.data;
                const pageKey: string = report.pageKeys[page];

                const placement = this.resolveNewComponentPlacement(pageKey, width, height);
                if (!placement) return this._snackbar.warning(COMPONENT_OVERLAP_WARNING);

                const element: Partial<GanttComponent> = {
                    pageKey,
                    height,
                    width,
                    filter: null,
                    x: placement.x,
                    y: placement.y,
                    type,
                };

                this._store.dispatch(new AddReportComponent(pageKey, element));
            }));
    }

    addPage() {
        if (!this.editModeCheck()) return;

        this._dialog.confirm(
            'Add Page',
            'Are you sure you want to add a new report page?',
            'Confirm',
            'Cancel',
            false,
            'reporting-theme'
        ).afterClosed().subscribe(res => {
            if (!res) return;

            this._store.dispatch(new AddReportPage)
        });

    }

    toggleEditMode(value: boolean) {
        this._store.dispatch(new ToggleEditMode(value));
    }

    toggleRightSidebar(current: RightSideBar) {
        if (this.rightActive == current) {
            this.rightActive = RightSideBar.UNSET;
            // this.rightDrawerRef.close();
        } else {
            this.rightActive = current;
            this.rightDrawerRef.open();
        }
    }

    openFilterDialog() {
        if (!this.editModeCheck('Filter updates')) return;

        const data: ReportFilterDialogData = {
            key: this.reportKey,
        }

        this._dialog.open(
            ReportFilterDialog, {
                data,
                panelClass: 'reporting-theme',
                width: '720px',
                maxWidth: '95vw',
                autoFocus: false
            }
        )
        .afterClosed()
        .subscribe(result => {
            if (!result) return;

            // A filter change only affects what each component compiles to, not the page/report
            // structure — sync the new filter locally (already persisted by the dialog) and
            // recompile components in place instead of reload()'s full report teardown/rebuild.
            this._store.dispatch(new SetReportFilter(this.reportKey, result.filter));
            this.recompileComponents();
        });
    }

    // Refreshes every component's compiled data without touching report/page status, so the
    // *ngFor of pages (and each page's components) stays mounted instead of remounting from scratch.
    recompileComponents() {
        const pageEntities = this._store.selectSnapshot(PageState.getEntities);

        Object.keys(pageEntities).forEach(pageKey => {
            const pageModel: PageModel = pageEntities[pageKey];
            pageModel.componentKeys.forEach(componentKey => {
                this._store.dispatch(new LoadComponentOptions(componentKey));
            });
        });
    }

    print() {
        this.toggleEditMode(false);
        // Wait for in-use report fonts so the print captures the real font, not a fallback.
        this._fontLoader.whenReady().then(() => window.print());
    }

    reload() {
        this._store.dispatch(new LoadReportGenerator());
    }

    shareReport(key: string, orgKey: string) {
        const data: AdvancedReportShareDialogData = {
            key,
            orgKey,
        };

        this._dialog.open(
            AdvancedReportShareDialog, {
                data,
                panelClass: 'reporting-theme',
                width: '480px',
                maxWidth: '95vw',
                autoFocus: false,
            }
        );
    }

    attempt() {
        console.log('Attempt')
    }

    back() {
        this._router.navigate([`/org/${this.ownerOrgKey}/generated-reports`]);
    }

    detectChanges() {
        return this._changes.markForCheck();
    }

    editModeCheck(context: string = "Report updates"): boolean {
        if (!this.editMode) {
            this._snackbar.error(`${context} restricted while in preview mode`);
            return false;
        }

        return true;
    }

}